import { DateHelper, formatCellValue } from "@swivl-mono/helpers";
import { CRMAccountStatus, CRMCustomerType, CRMUser } from "@swivl-mono/types";
import { Button, DataTable, DropdownOptionVariant, Panel, SearchInput } from "@swivl-mono/ui";
import { FilterDropdown } from "@swivl-mono/ui";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import { ActiveFilterChipSection, ActiveFilterChipSectionVariant } from "../../Components/ActiveFilterChipSection/ActiveFilterChipSection";
import { buildCRMSearchParams, parseOptionalNumber } from "../../Helpers/CRM/searchParams.Helper";
import { exportToCSV } from "../../Helpers/CSV/CSV.Helpers";
import useDebounce from "../../Hooks/useDebounce";
import { usePropertiesGetLocationIds } from "../../Hooks/usePropertiesGetLocationIds";
import { useUrlState } from "../../Hooks/useUrlState";
import CRMUsersModel from "../../Models/CRM/CRMUsers.Model";
import LocationsModel from "../../Models/Locations/Locations.Model";
import { NavBar } from "../../Models/NavBar/NavBar.Model";
import { UsersModel } from "../../Models/Users/Users.Model";
import UserVarsModel from "../../Models/Variables/UserVars.Model";
import UserDetails, { UserDetailsTabID } from "../ContactCenter/Conversations/DetailsColumn/UserDetails";
import { LocationsListComponent } from "../Properties/Components/Locations.List.Component";
import { CRMAddUserModal } from "./Components/CRM.AddUserModal";
import { CRMNavContent } from "./Components/CRM.NavTabs";

const CRMView = () => {
  const { users } = UsersModel.useState();
  const { currentData, isLoading, error, hasNext, hasPrev, sort, nextCursor, prevCursor } = CRMUsersModel.useState();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedValue: debouncedSearchTerm } = useDebounce<string>(searchTerm, 300);
  const page = usePropertiesGetLocationIds();
  const { getAllSearchParams, searchParams, setSearchParam, setMultipleSearchParams } = useUrlState();
  const selectedUserId = parseOptionalNumber(searchParams.get("userId"));
  const locationId = parseOptionalNumber(searchParams.get("locationId"));
  const viewAll = searchParams.get("viewAll") === "true";
  const groupId = parseOptionalNumber(searchParams.get("groupId"));
  const selectedUser = selectedUserId !== undefined ? users?.[selectedUserId] : undefined;
  const { vars } = UserVarsModel.useState((s) => s);

  const filterOptions = [
    {
      label: "Account",
      value: "account",
      options: [
        { label: "Has Autopay", value: "hasAutopay", variant: DropdownOptionVariant.WITH_CHECKBOX },
        { label: "Has Balance", value: "hasBalance", variant: DropdownOptionVariant.WITH_CHECKBOX }
      ]
    },
    {
      label: "Status",
      value: "status",
      isMultiSelect: false,
      options: [
        { label: "Current", value: "current", variant: DropdownOptionVariant.WITH_CHECKBOX },
        { label: "Past Due", value: "pastDue", variant: DropdownOptionVariant.WITH_CHECKBOX }
      ]
    },
    {
      label: "Customer Type",
      value: "customerType",
      isMultiSelect: false,
      options: [
        { label: "Lead", value: "lead", variant: DropdownOptionVariant.WITH_CHECKBOX },
        { label: "Tenant", value: "tenant", variant: DropdownOptionVariant.WITH_CHECKBOX }
      ]
    }
    // TODO: hide tags until we figure out how we manage them on the backend
    // {
    //   label: "Tag",
    //   value: "tag",
    //   options: [
    //     { label: "Lead", value: "lead", variant: DropdownOptionVariant.WITH_CHECKBOX },
    //     { label: "Prospect", value: "prospect", variant: DropdownOptionVariant.WITH_CHECKBOX }
    //   ]
    // }
  ];

  const columns: ColumnDef<CRMUser>[] = [
    { header: "First Name", accessorKey: "firstName", cell: (info) => formatCellValue(info.getValue() as string) },
    { header: "Last Name", accessorKey: "lastName", cell: (info) => formatCellValue(info.getValue() as string) },
    { header: "Phone", accessorKey: "phoneNumbers", cell: (info) => formatCellValue(info.getValue() as string[]), enableSorting: false },
    { header: "Email", accessorKey: "emails", cell: (info) => formatCellValue(info.getValue() as string[]), enableSorting: false },
    { header: "Balance", accessorKey: "totalBalance", cell: (info) => `$${(info.getValue() as number)?.toFixed(2) ?? "0.00"}`, enableSorting: false },
    { header: "Autopay", accessorKey: "autopay", cell: (info) => (info.getValue() ? "Yes" : "No"), enableSorting: false },
    // { header: "Tags", accessorKey: "tags", cell: (info) => formatCellValue(info.getValue() as string[]), enableSorting: false },
    {
      header: "Date Created",
      accessorKey: "createdAt",
      cell: (info) => formatCellValue(info.getValue() as string | null, (value) => (value ? DateHelper.formatDateMMDDYYYY(value) : "--"))
    },
    {
      header: "Last Active",
      accessorKey: "lastActivityDate",
      cell: (info) => formatCellValue(info.getValue() as string | null, (value) => (value ? DateHelper.formatDateMMDDYYYY(value) : "--"))
    }
  ];

  const selectedFiltersByGroup = useMemo(() => {
    const allParam = getAllSearchParams();

    return filterOptions.reduce<Record<string, string[]>>((acc, group) => {
      const paramValue = allParam[group.value];
      if (paramValue) {
        acc[group.value] = paramValue.split(",");
      }

      return acc;
    }, {});
  }, [searchParams.get("account"), searchParams.get("status"), searchParams.get("customerType")]);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);

    // initialize crm users model based on url params
    CRMUsersModel.initFromUrlParams(searchParams);

    NavBar.set(<CRMNavContent />, null, null, "CRM");

    // initialize limit param for pagination
    setSearchParam("limit", "100");
  }, []);

  // load selected user details when userid param changes
  useEffect(() => {
    if (selectedUserId) {
      UserVarsModel.loadForUser(selectedUserId);
      UsersModel.loadUsersByIds([selectedUserId]);
    }
  }, [selectedUserId]);

  // clear filters when location/group changes
  useEffect(() => {
    const filterParams: Record<string, undefined> = {
      account: undefined,
      status: undefined,
      customerType: undefined,
      search: undefined,
      before: undefined,
      after: undefined
    };
    setMultipleSearchParams(filterParams);
    setSearchTerm("");

    // clear model state directly without triggering load (the filter effect will handle that)
    CRMUsersModel.setState({
      hasAutopay: undefined,
      hasBalance: undefined,
      status: undefined,
      customerType: undefined,
      search: undefined
    });
  }, [locationId, groupId, viewAll]);

  // handle all filtering from url params in a single effect to avoid duplicate api calls
  useEffect(() => {
    const autoPay = selectedFiltersByGroup["account"]?.includes("hasAutopay");
    const balance = selectedFiltersByGroup["account"]?.includes("hasBalance");
    const statusFilters = selectedFiltersByGroup["status"]?.map((s) => (s === "pastDue" ? "past_due" : s)) as CRMAccountStatus[] | undefined;
    const status = statusFilters && statusFilters.length > 0 ? statusFilters : undefined;
    const customerTypeFilters = selectedFiltersByGroup["customerType"] as CRMCustomerType[] | undefined;
    const customerType = customerTypeFilters && customerTypeFilters.length > 0 ? customerTypeFilters : undefined;
    // const tag = selectedFiltersByGroup["tag"];

    // clear cursor params when location/group changes
    setMultipleSearchParams({ before: undefined, after: undefined });

    if (viewAll) {
      CRMUsersModel.setFilters({
        locationId: undefined,
        groupId: undefined,
        hasAutopay: autoPay,
        hasBalance: balance,
        status,
        customerType
      });
    } else {
      CRMUsersModel.setFilters({
        locationId,
        groupId,
        hasAutopay: autoPay,
        hasBalance: balance,
        status,
        customerType
      });
    }
  }, [locationId, groupId, viewAll, selectedFiltersByGroup]);

  // handle search term separately since it's debounced
  useEffect(() => {
    if (debouncedSearchTerm) {
      CRMUsersModel.setFilters({ search: debouncedSearchTerm });
      setSearchParam("search", debouncedSearchTerm);
    } else {
      CRMUsersModel.setFilters({ search: undefined });
      setSearchParam("search", undefined);
    }
  }, [debouncedSearchTerm]);

  const userVars = useMemo(() => (selectedUserId && vars ? vars[selectedUserId] : undefined), [vars, selectedUserId]);

  const handleSelectCell = (selectedUserId?: number) => {
    setSearchParam("userId", selectedUserId !== undefined ? String(selectedUserId) : undefined);
  };

  const handleSetFilters = (parent: string, filters: string[]) => {
    setSearchParam(parent, filters.length > 0 ? filters.join(",") : undefined);
  };

  const handleClearFilter = (group?: string, selectedFilter?: string) => {
    if (group && selectedFilter) {
      const currentFilters = selectedFiltersByGroup[group] ?? [];
      const nextFilters = currentFilters.filter((f) => f !== selectedFilter);
      setSearchParam(group, nextFilters?.length > 0 ? nextFilters.join(",") : undefined);
    } else if (group && !selectedFilter) {
      // clear entire group
      setSearchParam(group, undefined);
    }
  };

  const handleClearFilters = () => {
    const clearedParams: Record<string, undefined> = {
      hasAutopay: undefined,
      hasBalance: undefined,
      status: undefined,
      customerType: undefined
    };

    filterOptions.forEach((group) => {
      clearedParams[group.value] = undefined;
    });

    setMultipleSearchParams(clearedParams);

    // clear filters in the model
    CRMUsersModel.setFilters({
      hasAutopay: undefined,
      hasBalance: undefined,
      status: undefined,
      customerType: undefined
    });
  };

  const initialSorting: SortingState = useMemo(() => {
    return sort.map((s) => {
      const [id, direction] = s.split(":");
      return { id, desc: direction === "desc" };
    });
  }, [sort]);

  // convert tanstack format to our model format and sync to url
  const handleSortingChange = async (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    // tanstack passes the updater function when user clicks to change sorting
    const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(initialSorting) : updaterOrValue;
    const sortStrings = newSorting?.map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`);
    await CRMUsersModel.setSort(sortStrings);
  };

  // pagination handlers
  const handlePrev = async () => {
    await CRMUsersModel.previousPage();
    setMultipleSearchParams({ before: String(prevCursor), after: undefined });
  };

  const handleNext = async () => {
    await CRMUsersModel.nextPage();
    setMultipleSearchParams({ after: String(nextCursor), before: undefined });
  };

  const handleSelectRow = (selectedIds: string[]) => {
    setSelectedRows(selectedIds);
  };

  const handleExportToCSV = () => {
    const csvHeaders = ["First Name", "Last Name", "Phone", "Email", "Balance", "Autopay", "Date Created", "Last Active"];
    // filter users based on selected rows
    const usersToExport = selectedRows.length > 0 ? currentData.filter((user) => selectedRows.includes(String(user.id))) : currentData;

    // build csv data as array of objects keyed by header names
    const csvData = usersToExport.map((user) => {
      return {
        "First Name": formatCellValue(user.firstName),
        "Last Name": formatCellValue(user.lastName),
        Phone: formatCellValue(user.phoneNumbers?.[0]),
        Email: formatCellValue(user.emails?.[0]),
        Balance: `$${user.totalBalance?.toFixed(2) ?? "0.00"}`,
        Autopay: user.autopay ? "Yes" : "No",
        "Date Created": formatCellValue(user.createdAt),
        "Last Active": formatCellValue(user.lastActivityDate)
      };
    });

    exportToCSV(csvData, csvHeaders, "crm_users");
  };

  const handleClear = () => {
    setSearchTerm("");
    setSearchParam("search", undefined);
  };

  return (
    <>
      <div className="flex size-full divide-x divide-grey-200">
        <div className="w-[300px] shrink-0 overflow-y-auto bg-white">
          <LocationsListComponent
            {...page}
            prependLinks={[
              { name: "No Location", url: { search: buildCRMSearchParams({ level: "location", locationId: "none", groupId: "none" }, searchParams) } }
            ]}
          />
        </div>
        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* filters and actions */}
          <div className="flex h-[55px] items-center justify-between border-b border-grey-400 bg-grey-50 px-3 py-2.5">
            <div className="flex items-center gap-2">
              {/* input */}
              <SearchInput placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onClear={handleClear} />
              <FilterDropdown
                filterOptions={filterOptions}
                selectedFilters={selectedFiltersByGroup}
                onChangeFilters={handleSetFilters}
                onClearAllFilters={handleClearFilters}
              />
              {/* export and sms campaign buttons */}
              {selectedRows.length > 0 && (
                <>
                  <Button onClick={handleExportToCSV}>Export</Button>
                  {/* <SMSCampaignButton preselectedTenantIds={selectedRows.map(Number)} color={ButtonColor.BLACK} size={ButtonSize.MEDIUM} /> */}
                  <span className="body-sm whitespace-nowrap">{selectedRows.length} items selected</span>
                </>
              )}
            </div>
            <div className="flex items-center">
              {/* add user modal & button */}
              <CRMAddUserModal />
            </div>
          </div>

          {/* filter chips section */}
          <ActiveFilterChipSection
            classNames={{ wrapper: "border-b border-grey-400" }}
            filterOptions={filterOptions}
            selectedFiltersByGroup={selectedFiltersByGroup}
            handleClearFilter={handleClearFilter}
            handleClearFilters={handleClearFilters}
            handleSetFilters={handleSetFilters}
            variant={ActiveFilterChipSectionVariant.INLINE}
          />

          {/* data table */}
          <div className="min-h-0 flex-1">
            <DataTable
              loading={isLoading}
              columns={columns}
              data={currentData}
              error={error}
              handleSelectCell={handleSelectCell}
              areRowsSelectable
              hasCheckboxColumn
              initialSorting={initialSorting}
              manualConfig={{
                manualSorting: true,
                manualPagination: true,
                manualFiltering: true,
                useCursorPagination: true
              }}
              onSelectedIdsChange={handleSelectRow}
              paginationState={{
                hasNext,
                hasPrev,
                onNext: handleNext,
                onPrev: handlePrev
              }}
              onSortingChange={handleSortingChange}
              selectedRowId={selectedUserId !== undefined ? String(selectedUserId) : null}
            />
          </div>
        </div>
      </div>
      <Panel
        isOpen={!!selectedUser}
        onToggle={() => {
          setSearchParam("userId", undefined);
        }}
      >
        {selectedUser && (
          <UserDetails
            tabs={[
              { id: UserDetailsTabID.Details, label: "Customer Details", badge: false },
              { id: UserDetailsTabID.Notes, label: "Notes", badge: false }
            ]}
            user={selectedUser}
            userVars={userVars}
          />
        )}
      </Panel>
    </>
  );
};

export default CRMView;
